const CLIENT_ID_KEY = "clientID";

/// This tab's own session id, `null` until something reports it. The tab's own
/// login writes `clientID` directly and a same-tab `setItem` fires no `storage`
/// event, so the writer has to hand it over.
let known: string | null = null;
/// Whether this tab holds a session at all — a signed-out tab only cares about
/// a session appearing.
let authenticated = false;

/// Reloads this tab when another tab ends or replaces the session. Logout
/// reloads only its own tab, so a tab left open would keep polling its old org
/// under the next account's cookie.
export function installSessionWatcher(
  reload: () => void = () => window.location.reload(),
): () => void {
  known = readClientId();
  authenticated = false;
  const onStorage = (e: StorageEvent) => {
    if (e.storageArea && e.storageArea !== localStorage) return;
    // `key === null` is `localStorage.clear()`, which is how logout ends a session.
    if (e.key !== null && e.key !== CLIENT_ID_KEY) return;
    const next = e.key === null ? null : e.newValue;
    // One session per browser, so a tab showing a different one is wrong in
    // both directions: signed in after the session ended or changed, and
    // signed out after one appeared. A same-tab write fires no event, so this
    // is always another tab. `known === null` while authenticated means the id
    // was never learned, and the tab cannot tell this change from its own.
    if (known === null && !authenticated && next === null) return;
    if (known !== null && next === known) return;
    reload();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/// Called by whoever writes `clientID`.
export function noteSessionClientId(id: string): void {
  known = id;
  authenticated = true;
}

/// Called by the auth check that gates every route. It runs on the canvas too,
/// where the shell that writes `clientID` never mounts.
export function noteSessionAuthenticated(isAuthenticated: boolean): void {
  authenticated = isAuthenticated;
  if (!isAuthenticated) known = null;
}

export function readClientId(): string | null {
  try {
    return localStorage.getItem(CLIENT_ID_KEY);
  } catch {
    return null;
  }
}
